import { Component, EventEmitter } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { filter, finalize, map } from 'rxjs/operators';
import { CustomerAccountService } from '../../../customer/account/service/customer-account.service';
import { Jurisdiction } from '../../../foundation/models/enums/jurisdiction.enum';
import { ModalSize } from '../../../shared/services/modal/mvd-modal.enum';
import { MvdModalService } from '../../../shared/services/modal/mvd-modal.service';
import { DisbursementRequestCustomerAccountModel } from '../../models/disbursement/disbursement-request-customer-account.model';
import { DisbursementRequestModel } from '../../models/disbursement/disbursement-request.model';
import { FinanceDisbursementRequestStatusType } from '../../models/enums/finance-disbursement-request-status-type.enum';
import { FinanceDisbursementRequestType } from '../../models/enums/finance-disbursement-request-type.enum';
import { DisbursementService } from '../../services/disbursement.service';
import { FinanceBaseComponent } from '../../shared/components/finance-base-component/finance-base.component';
import { DisbursementRequestConfirmationModalComponent, DisbursementRequestData } from './disbursement-request-confirmation-modal.component';
import { ValidationModel } from '../../../shared/models/validation-results/validation.model';
import { ValidationResultModel } from '../../../shared/models/validation-results/validation-result.model';
import { ValidationStateModel } from '../../../shared/models/validation-results/validation-state.model';
import { CustomerNameType } from '../../../customer/models/enums/customer-name-type.enum';
import { CustomerName } from '../../../customer/models/customer-name.model';
import { CustomerAccountDetail } from '../../../customer/models/customer-account-detail.model';

@Component({
    selector: 'disbursement-request',
    templateUrl: './disbursement-request.html',
    outputs: ['navigateBack'],
    inputs: ['customerAccountNumberId', 'customerNumberId', 'names']
})
export class DisbursementRequestComponent extends FinanceBaseComponent  {
    // An enum representing the different categories of names a customer might have (e.g. legal name, organization name)
    private customerNameType = new CustomerNameType();

    // Gets or sets the form.
    public form: UntypedFormGroup;

    // Gets or sets the customer account number id
    public customerAccountNumberId: number;

    // Gets or sets the disbursement request id
    public disbursementRequestId: number;

    // Gets or sets the customer number id
    public customerNumberId: number;

    // Gets or sets the names array
    public names: CustomerName[] = [];

    // Gets or sets the display name
    public displayName: string;

    // Gets or sets the back event emitter
    public navigateBack = new EventEmitter<void>();

    // Gets or sets the customer account detail
    public customerAccountDetail: CustomerAccountDetail;

    // Gets or sets the show page flag
    public showPage: boolean;

    // Gets or sets the disbursement submitted flag
    public disbursementSubmitted: boolean;

    // Gets or sets the new account balance
    public newAccountBalance: number;

    // Gets or sets the jurisdiction
    private jurisdictions = new Jurisdiction();

    // Gets or sets the show confirmation flag
    public showConfirmation: boolean;

    // A user-friendly representation of the customer's jurisdiction
    public jurisdictionDisplayValue = '';

    constructor(
        private formBuilder: UntypedFormBuilder,
        private modalService: MvdModalService,
        private disbursementService: DisbursementService,
        private customerAccountService: CustomerAccountService
    ) {
        super();
    }

    /**
      * Initializes component
      */
    public ngOnInit() {
        this.UseHelpContent('#CONTENT/AZ_MVD_Now/Disbursement_Request.htm%3FTocPath%3DAZ%2520MVD%2520Now%7C_____5');
        this.initPageData();
    }

    /**
     * Initializes the form
     **/
    private createForm(): void {
        this.form = this.formBuilder.group({
            amount: [null, [Validators.required, Validators.min(0.01), Validators.max(this.customerAccountDetail.accountBalance)]]
        });
    }

    /**
     * Initializes the required page data
     */
    protected initPageData() {
        this.showPage = false;
        this.customerAccountService.getCustomerAccountDetails(this.customerAccountNumberId)
            .pipe(finalize(() => this.showPage = true))
            .subscribe(r => this.setPageData(r));
    }

    /**
     *
     * @param data
     */
    protected setPageData(data: CustomerAccountDetail) {
        this.customerAccountDetail = data;
        this.displayName = this.getDisplayName();
        this.createForm();

        if (this.customerAccountDetail.accountAddress == null) {
            const validationModel: ValidationModel = new ValidationModel([]);

            const val: ValidationResultModel = new ValidationResultModel();
            const state: ValidationStateModel = new ValidationStateModel();
            state.validationType = 'Error';
            val.errorMessage = 'The customer does not have a valid mailing address associated to their customer account.';
            val.source = 'Model';
            val.state = state;

            validationModel.validationResults.push(val);

            this.handleValidationResult(validationModel);
        }

        if (this.customerAccountDetail.isRedactEnabled && this.customerAccountDetail.accountAddress) {
            this.customerAccountDetail.accountAddress.jurisdictionId = null;
            this.customerAccountDetail.accountAddress.countyId = null;
        } else if (this.customerAccountDetail.accountAddress?.jurisdictionId) {
            this.jurisdictionDisplayValue = this.jurisdictions.FromId(this.customerAccountDetail.accountAddress.jurisdictionId).displayValue;
        }
    }

    /**
     * Gets the customer's full name.
     * A customer is supposed to always have exactly one legal name (if they are an individual) or
     * exactly one organization name (if they are an organization).
     * The full name is taken from that.
     * Note: this method would be more aptly placed inside a cross-functional service somewhere
     * @returns the customer's full name
     */
    private getDisplayName(): string {
        const nameTypes = [this.customerNameType.LegalName, this.customerNameType.OrganizationName].map(t => t.id);
        const name = this.names.find(n => nameTypes.includes(n.customerNameTypeId));

        return name?.displayName;
    }

    /**
     * Handles the cancel event
     */
    public cancel() {
        this.navigateBack.emit();
    }

    /**
     * Launches the disbursement summary modal
     * @param financeDisbursementRequestId the finance disbursement request id
     */
    public launchConfirmDisbursementModal() {
        const data = new DisbursementRequestData(this.customerAccountDetail.accountBalance, this.form.get('amount').value, this.displayName);
        this.modalService.open(DisbursementRequestConfirmationModalComponent, data, ModalSize.Medium).afterClosed()
            .pipe(filter(result => !!result)).subscribe(() => this.submitDisbursementRequest());
    }

    private submitDisbursementRequest() {
        this.disbursementSubmitted = true;
        const disbursementRequestCustomerAccount = new DisbursementRequestCustomerAccountModel('');
        disbursementRequestCustomerAccount.amount = this.form.get('amount').value;
        disbursementRequestCustomerAccount.customerAccountNumberId = this.customerAccountNumberId;

        const disbursementRequest = new DisbursementRequestModel('');
        disbursementRequest.customerAccounts = new Array<DisbursementRequestCustomerAccountModel>();
        disbursementRequest.customerAccounts.push(disbursementRequestCustomerAccount);
        disbursementRequest.amount = this.form.get('amount').value;
        disbursementRequest.financeDisbursementRequestTypeId = new FinanceDisbursementRequestType().CheckDisbursement.id;
        disbursementRequest.financeDisbursementRequestStatusTypeId = new FinanceDisbursementRequestStatusType().Pending.id;
        disbursementRequest.customerNumberId = this.customerNumberId;
        disbursementRequest.comment = 'Created via MAX';

        this.disbursementService.saveDisbursementRequest(disbursementRequest)
            .pipe(
                filter(r => r.isValid),
                map(r => +r.result),
                finalize(() => this.disbursementSubmitted = false)
            )
            .subscribe({
                next: r => this.showConfirmationView(r),
                error: e => this.handleValidationResult(e)
            });
    }

    private showConfirmationView(disbursementRequestId: number) {
        this.disbursementRequestId = disbursementRequestId;
        this.newAccountBalance = this.customerAccountDetail.accountBalance - this.form.get('amount').value;
        this.showConfirmation = true;
    }
}
